import { PayloadAction, createSlice } from "@reduxjs/toolkit";
import {
  DEFAULT_RETRY,
  NETWORK_ERROR_RETRY,
  isNetworkErroFunc,
} from "../../../utils/calculateCartHepler";
import CartItem from "../../../utils/CartItem";
import { SuccessReportTypes } from "../../../utils/ErrorTypes";
import { DoubleLinkedList } from "../../../utils/help/linkedList/LinkedList";
import {
  nextBogoStep,
  previousBogoStep,
} from "../../../utils/help/offers/offers";
import Offer from "../../../utils/OfferClass/Offer/Offer";
import { createOffer } from "../../../utils/OfferClass/OfferClassUtils/OfferCreator/offerCreator";
import { OutOfStockItem } from "../../../utils/types";
import { calculateCartWithOffersCall } from "../http/cartAndLoyaltyV2";
import {
  CartAction,
  CartActionType,
  CartItemType,
  CartLoyaltyV2SliceType,
  RedirectToItem,
} from "../types/CartLoyaltyV2Types";
import { replaceNullWithUndefined } from "../types/TypeHelper";

const initialState: CartLoyaltyV2SliceType = {
  cart: {
    items: [],
    subTotalPrice: 0,
    deliveryFees: 0,
    vat: 0,
    service: 0,
    tips: 0,
    totalPrice: 0,
    cartItemsNumber: 0,
    redeemedOffer: undefined,
    isEligibleOffer: false,
    remainingToEligiable: 0,
    discountedValue: 0,
  },
  cartActions: [],
  redirectToItem: undefined,
  selectedOfferId: -1,
  isLoading: false,
  pendingOperations: 0,
  selectedBuySubItem: undefined,
  selectedOfferSubItems: new DoubleLinkedList<CartItemType>(),
  selectedParentOffer: undefined,
  bogoSteps: undefined,
};

const cartLoyaltyV2 = createSlice({
  name: "cartLoyaltyV2",
  initialState,
  reducers: {
    addItemAction: (state, action: PayloadAction<CartItem>) => {
      let cartItem = action.payload.toJson();
      cartItem = replaceNullWithUndefined(cartItem) as CartItemType;
      const cartAction: CartAction = {
        actionType: CartActionType.ADD_ITEM,
        item: cartItem,
      };
      state.cartActions.push(cartAction);
    },
    removeItemAction: (
      state,
      action: PayloadAction<{
        itemIndex: number | number[];
      }>
    ) => {
      const cartAction: CartAction = {
        actionType: CartActionType.REMOVE_ITEM,
        itemIndex: action.payload.itemIndex,
      };
      state.cartActions.push(cartAction);
    },
    editItemAction: (
      state,
      action: PayloadAction<{ itemIndex: number; item: CartItem }>
    ) => {
      let cartItem = action.payload.item.toJson();
      cartItem = replaceNullWithUndefined(cartItem) as CartItemType;
      const cartAction: CartAction = {
        actionType: CartActionType.EDIT_ITEM,
        itemIndex: action.payload.itemIndex,
        item: cartItem,
      };
      state.cartActions.push(cartAction);
    },
    setTipAction: (state, action: PayloadAction<number>) => {
      state.cart.tips = action.payload;
      // const cartAction: CartAction = {
      //   actionType: CartActionType.TIPS,
      //   amount: action.payload,
      // };
      // state.cartActions.push(cartAction);
    },
    resetCartAction: (state) => {
      state.cartActions = [];
      state.cart = initialState.cart;
    },
    redeemOfferAction: (
      state,
      action: PayloadAction<{
        offer: Offer;
        item?: CartItem[];
        franchiseId?: number;
        extraDetails?: { isFromItemFromList?: boolean };
      }>
    ) => {
      if (!(action.payload.offer instanceof Offer)) {
        return;
      }
      const isFromItemFromList =
        action.payload.extraDetails?.isFromItemFromList;
      let payloadDetails = action.payload.offer.applyOffer(
        state,
        action.payload.item,
        undefined,
        { isNotFirstApply: isFromItemFromList }
      );

      if (payloadDetails) {
        // Check if the offer is a sub offer
        if (action.payload.offer.isSubOffer) {
          if (!state.selectedParentOffer || !state.selectedBuySubItem) {
            return;
          }

          // Apply Parent Offer with sub offer
          const parentOffer = createOffer(state.selectedParentOffer);
          const parentOfferPayload = parentOffer.applyOffer(
            state,
            [state.selectedBuySubItem as CartItem],
            payloadDetails
          );

          if (!parentOfferPayload) {
            return;
          }

          payloadDetails = parentOfferPayload;
          state.selectedOfferSubItems = new DoubleLinkedList<CartItemType>();
          state.selectedParentOffer = undefined;
          state.selectedBuySubItem = undefined;
          state.bogoSteps = undefined;
        }

        const cartAction: CartAction = {
          actionType: CartActionType.REDEEM_OFFER,
          data: payloadDetails,
        };
        state.cartActions.push(cartAction);
      }
    },
    removeOfferAction: (state) => {
      const cartAction: CartAction = {
        actionType: CartActionType.REMOVE_OFFER,
      };
      state.redirectToItem = undefined;
      state.selectedOfferId = -1;
      state.cartActions.push(cartAction);
      state.selectedOfferSubItems = new DoubleLinkedList<CartItemType>();
      state.selectedBuySubItem = undefined;
    },
    setRedirectItem: (
      state,
      action: PayloadAction<RedirectToItem | undefined>
    ) => {
      state.redirectToItem = action.payload;
    },
    setSelectedOfferId: (state, action: PayloadAction<number>) => {
      state.selectedOfferId = action.payload;
    },
    setSelectedBuySubItem: (
      state,
      action: PayloadAction<CartItemType | undefined>
    ) => {
      state.selectedBuySubItem = action.payload;
    },

    setSelectedOfferSubItems: (
      state,
      action: PayloadAction<DoubleLinkedList<CartItemType>>
    ) => {
      state.selectedOfferSubItems = action.payload;
    },
    nextBogoStepAction: (state) => {
      state.bogoSteps = nextBogoStep(state.bogoSteps);
    },
    previousBogoStepAction: (state) => {
      state.bogoSteps = previousBogoStep(state.bogoSteps);
    },
    receiptErrorAction: (
      state,
      action: PayloadAction<
        | {
            errorType:
              | SuccessReportTypes.ReceiptController_ConfirmReceipt_ReqItemsContainsOutDatedItems
              | SuccessReportTypes.ItemService_ValidateOutOfStockSubItemIds_ReqItemsContainsOutOfStock;
            subItemIds: number[];
            outOfStockItems: [];
          }
        | {
            errorType: SuccessReportTypes.ReceiptController_ConfirmReceipt_itemsQuantityOutOfStock;
            outOfStockItems: OutOfStockItem[];
            subItemIds: [];
          }
      >
    ) => {
      const cartAction: CartAction = {
        actionType: CartActionType.CREATE_RECEIPT_ERROR,
        error: {
          errorType: action.payload.errorType,
          subItemIds: action.payload.subItemIds,
          outOfStockItems: action.payload.outOfStockItems,
        },
      };
      state.cartActions.push(cartAction);
    },
    resetLoading: (state) => {
      state.isLoading = false;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(calculateCartWithOffersCall.pending, (state) => {
      state.isLoading = true;
      state.pendingOperations = (state.pendingOperations || 0) + 1;
    });

    builder.addCase(
      calculateCartWithOffersCall.fulfilled,
      (state, { payload }) => {
        if (payload) {
          state.cart = { ...payload, tips: state.cart.tips };
        }
        state.pendingOperations = Math.max(
          0,
          (state.pendingOperations || 1) - 1
        );

        state.isLoading = state.pendingOperations > 0;
      }
    );

    builder.addCase(
      calculateCartWithOffersCall.rejected,
      (state, { error, meta }) => {
        state.pendingOperations = Math.max(
          0,
          (state.pendingOperations || 1) - 1
        );

        state.isLoading = state.pendingOperations > 0;
        const errMsg = JSON.stringify(error.message);
        const currentRetryCount = meta?.arg?.retryCount || 0;
        const isNetworkError = isNetworkErroFunc(errMsg);

        // For non-network errors on first try, pop the last action
        // The middleware will handle retrying with the remaining actions
        if (!isNetworkError && currentRetryCount < DEFAULT_RETRY) {
          state.cartActions.pop();
        }

        // Reset the cart if we've reached max retries
        // The middleware won't retry anymore in this case
        if (
          (isNetworkError && currentRetryCount >= NETWORK_ERROR_RETRY) ||
          (!isNetworkError && currentRetryCount >= DEFAULT_RETRY) ||
          state.cartActions.length === 0
        ) {
          // Log the error
          console.log("Cart reset due to max retries or empty cart");

          // Reset cart state
          state.cartActions = [];
          state.cart = initialState.cart;
        }
      }
    );
  },
});

export const {
  addItemAction,
  removeItemAction,
  editItemAction,
  setTipAction,
  resetCartAction,
  redeemOfferAction,
  removeOfferAction,
  setRedirectItem,
  setSelectedOfferId,
  receiptErrorAction,
  setSelectedBuySubItem,
  setSelectedOfferSubItems,
  resetLoading,
  nextBogoStepAction,
  previousBogoStepAction,
} = cartLoyaltyV2.actions;

export default cartLoyaltyV2.reducer;
